// Process block directives (containers)
// :::spoiler
// He dies.
// :::
//
// Fences may use 3+ colons. Close fence must have >= open fence length.
// This lets outer `::::` contain inner `:::` without depth tracking.

import type StateBlock from '../state-block'
import { parseAttributeBlock } from '../../helpers/parse-attribute-block'

function countColons(str: string, start: number, max: number): number {
  let n = 0
  while (start + n < max && str.charCodeAt(start + n) === 0x3a /* : */) n++
  return n
}

export default function directive_block(
  state: StateBlock,
  startLine: number,
  endLine: number,
  silent: boolean,
): boolean {
  let pos = state.bMarks[startLine] + state.tShift[startLine]
  const max = state.eMarks[startLine]

  const fenceLen = countColons(state.src, pos, max)
  if (fenceLen < 3) return false

  pos += fenceLen

  // Parse the directive line
  const directiveLine = state.src.slice(pos, max).trim()
  const directiveMax = directiveLine.length

  // Parse name (required)
  let directivePos = 0
  while (directivePos < directiveMax) {
    const code = directiveLine.charCodeAt(directivePos)
    if (
      (code >= 0x30 && code <= 0x39) || // 0-9
      (code >= 0x41 && code <= 0x5a) || // A-Z
      (code >= 0x61 && code <= 0x7a) || // a-z
      code === 0x2d || // -
      code === 0x5f // _
    ) {
      directivePos++
    } else {
      break
    }
  }

  const name = directiveLine.slice(0, directivePos)
  if (name.length === 0) return false

  // Name cannot end with - or _
  const lastChar = name[name.length - 1]
  if (lastChar === '-' || lastChar === '_') return false

  // Parse optional bracket content before attributes.
  let content: string | undefined
  if (
    directivePos < directiveMax &&
    directiveLine.charCodeAt(directivePos) === 0x5b /* [ */
  ) {
    const contentStart = directivePos + 1
    let depth = 1
    directivePos++

    while (directivePos < directiveMax && depth > 0) {
      const code = directiveLine.charCodeAt(directivePos)
      if (code === 0x5b /* [ */) depth++
      else if (code === 0x5d /* ] */) depth--

      if (depth > 0) directivePos++
    }

    if (depth !== 0) return false

    content = directiveLine.slice(contentStart, directivePos)
    directivePos++
  }

  let attributes: Record<string, string> = {}
  if (directivePos < directiveMax) {
    const attrRes = parseAttributeBlock(
      directiveLine,
      directivePos,
      directiveMax,
    )
    if (attrRes.ok) {
      attributes = attrRes.attributes
    }
  }

  if (silent) return true

  const markup = ':'.repeat(fenceLen)

  const token_open = state.push('directive_block_open', 'div', 1)
  token_open.markup = markup
  token_open.meta = { name, attributes, content }

  // Directives with bracket content are self-closing (no separate closing fence)
  const isSelfClosing = !!content

  if (isSelfClosing) {
    token_open.map = [startLine, startLine + 1]

    const token_close = state.push('directive_block_close', 'div', -1)
    token_close.markup = markup
    token_close.hidden = true

    state.line = startLine + 1
  } else {
    // Regular directive - search for closing fence of length >= fenceLen
    const oldParent = state.parentType
    const oldLineMax = state.lineMax
    state.parentType = 'container'

    let nextLine = startLine
    let haveEndMarker = false

    while (nextLine < endLine) {
      nextLine++
      if (nextLine >= endLine) break

      const lineStart = state.bMarks[nextLine] + state.tShift[nextLine]
      const lineMax = state.eMarks[nextLine]

      // Closing fence: a line that is only colons (>= fenceLen), optionally trailing whitespace
      const lineColons = countColons(state.src, lineStart, lineMax)
      if (lineColons >= fenceLen) {
        // Rest of the line (after colons) must be whitespace only
        const rest = state.src.slice(lineStart + lineColons, lineMax)
        if (/^\s*$/.test(rest)) {
          haveEndMarker = true
          break
        }
      }
    }

    token_open.map = [startLine, nextLine]
    token_open.meta.rawContent = state.getLines(
      startLine + 1,
      nextLine,
      0,
      false,
    )

    // Parse the content inside the directive
    const oldMin = state.blkIndent
    // Keep the enclosing list item's indentation: its content is still in the source.
    // Resetting to zero turns nested directives' children into indented code blocks.
    state.lineMax = nextLine

    state.md.block.tokenize(state, startLine + 1, nextLine)

    state.blkIndent = oldMin
    state.lineMax = oldLineMax
    state.parentType = oldParent

    const token_close = state.push('directive_block_close', 'div', -1)
    token_close.markup = markup

    state.line = nextLine + (haveEndMarker ? 1 : 0)
  }

  return true
}
